/**
 * Bigens Subscription Selector
 * Sistema de seleção de assinatura via variantes para Shopify
 * Versão: 1.0.0
 */

class BigensSubscriptionSelector {
  constructor(config = {}) {
    this.config = {
      containerSelector: config.containerSelector || '#bigens-subscription-selector',
      variantPickerSelector: config.variantPickerSelector || 'variant-main-picker, variant-radios, variant-selects',
      productFormSelector: config.productFormSelector || 'product-form',
      // Configurações de benefícios
      benefits: config.benefits || [
        { icon: '✓', text: 'Frete grátis', subtext: 'em todos envios' },
        { icon: '✓', text: 'Cancele ou altere', subtext: 'quando quiser' },
        { icon: '✓', text: 'Brindes exclusivos', subtext: 'o ano todo' }
      ],
      // Configurações de desconto
      discountBadge: config.discountBadge || '10% OFF',
      // Mapeamento de variantes para períodos
      subscriptionMap: config.subscriptionMap || {
        'mensal': 30,
        '30': 30,
        'bimestral': 60,
        '60': 60,
        'trimestral': 90,
        '90': 90
      }
    };

    this.state = {
      selectedType: 'one-time', // 'one-time' ou 'subscription'
      selectedPeriod: 30, // 30, 60 ou 90 dias
      productData: null,
      variants: {
        oneTime: null,
        subscriptions: []
      },
      // Controle do modal de termos (adicionado v1.1)
      modalOpen: false,
      pendingBtn: null
    };

    this.init();
  }

  init() {
    this.loadProductData();
    this.identifyVariants();
    this.render();
    this.attachEventListeners();
    this.observeVariantChanges();
    this.interceptAddToCart(); // v1.1: intercepta add-to-cart quando assinatura ativa
  }

  loadProductData() {
    // Tenta carregar dados do produto do objeto global do Shopify
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta) {
      this.state.productData = window.ShopifyAnalytics.meta.product;
    }
    
    // Fallback: busca do JSON do produto
    const productJsonElement = document.querySelector('[data-product-json]');
    if (productJsonElement) {
      try {
        this.state.productData = JSON.parse(productJsonElement.textContent);
      } catch (e) {
        console.error('Erro ao carregar dados do produto:', e);
      }
    }

    // Última tentativa: busca direto da página
    if (!this.state.productData) {
      const productElement = document.querySelector('[data-product-id]');
      if (productElement) {
        const productId = productElement.dataset.productId;
        this.fetchProductData(productId);
      }
    }
  }

  async fetchProductData(productId) {
    try {
      const response = await fetch(`/products/${window.location.pathname.split('/products/')[1]}.js`);
      this.state.productData = await response.json();
      this.identifyVariants();
      this.render();
    } catch (e) {
      console.error('Erro ao buscar dados do produto:', e);
    }
  }

  identifyVariants() {
    if (!this.state.productData || !this.state.productData.variants) return;

    const variants = this.state.productData.variants;
    const periodsFound = {}; // Rastreia períodos já encontrados
    
    // Identifica variantes de assinatura e compra única
    variants.forEach(variant => {
      const title = variant.title.toLowerCase();
      const option1 = variant.option1?.toLowerCase() || '';
      const option2 = variant.option2?.toLowerCase() || '';
      const option3 = variant.option3?.toLowerCase() || '';
      
      const allOptions = `${title} ${option1} ${option2} ${option3}`;
      
      // Verifica se é assinatura
      let isSubscription = false;
      let period = null;

      Object.keys(this.config.subscriptionMap).forEach(key => {
        if (allOptions.includes(key)) {
          isSubscription = true;
          period = this.config.subscriptionMap[key];
        }
      });

      if (isSubscription && period) {
        // Apenas adiciona se esse período ainda não foi encontrado
        if (!periodsFound[period]) {
          this.state.variants.subscriptions.push({
            ...variant,
            period: period
          });
          periodsFound[period] = true;
        }
      } else if (allOptions.includes('única') || allOptions.includes('compra única') || 
                 (!isSubscription && this.state.variants.subscriptions.length === 0)) {
        // Se não encontrou marcador de assinatura, assume como compra única
        if (!this.state.variants.oneTime) {
          this.state.variants.oneTime = variant;
        }
      }
    });

    // Ordena assinaturas por período
    this.state.variants.subscriptions.sort((a, b) => a.period - b.period);

    // Se não encontrou compra única, usa a primeira variante que não é assinatura
    if (!this.state.variants.oneTime && this.state.variants.subscriptions.length > 0) {
      const nonSubVariants = variants.filter(v => 
        !this.state.variants.subscriptions.find(s => s.id === v.id)
      );
      if (nonSubVariants.length > 0) {
        this.state.variants.oneTime = nonSubVariants[0];
      }
    }

    console.log('Variantes identificadas:', this.state.variants);
  }

  calculateShipmentDates(startDate, period, count = 3) {
    const dates = [];
    const start = new Date(startDate);

    for (let i = 0; i < count; i++) {
      const date = new Date(start);
      date.setDate(date.getDate() + (period * i));
      dates.push(date);
    }

    return dates;
  }

  formatDate(date) {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  }

  renderOneTimeOption() {
    const isSelected = this.state.selectedType === 'one-time';
    const borderColor = isSelected ? '#E7411B' : '#DAD9CE';
    const borderWidth = isSelected ? '2px' : '1px';
    const radioColor = isSelected ? '#E7411B' : '#8A836C';
    const radioFill = isSelected ? `<div style="width: 8px; height: 8px; left: 7px; top: 7px; position: absolute; background: ${radioColor}; border-radius: 999px"></div>` : '';

    return `
      <div class="bigens-option bigens-one-time ${isSelected ? 'selected' : ''}" 
           style="align-self: stretch; padding: 20px; background: white; border-radius: 14px; outline: ${borderWidth} ${borderColor} solid; outline-offset: -2px; justify-content: flex-start; align-items: center; gap: 18px; display: flex; cursor: pointer; transition: all 0.2s ease;"
           data-option-type="one-time">
        <div style="width: 22px; height: 24px; padding-top: 2px; flex-direction: column; justify-content: flex-start; align-items: flex-start; display: inline-flex;">
          <div style="width: 22px; height: 22px; position: relative; border-radius: 999px; outline: 2px ${radioColor} solid; outline-offset: -2px;">
            ${radioFill}
          </div>
        </div>
        <div style="flex: 1 1 0; flex-direction: column; justify-content: flex-start; align-items: flex-start; gap: 1px; display: inline-flex;">
          <div style="align-self: stretch; color: #27272A; font-size: 16px; font-family: Poppins, sans-serif; font-weight: 600; line-height: 15.40px;">
            Compra única
          </div>
          <div style="color: #27272A; font-size: 14px; font-family: Poppins, sans-serif; font-weight: 400; line-height: 17.50px;">
            Parcele em até 6x sem juros
          </div>
        </div>
      </div>
    `;
  }

  renderSubscriptionOption() {
    const isSelected = this.state.selectedType === 'subscription';
    const borderColor = isSelected ? '#F13E1A' : '#DAD9CE';
    const borderWidth = isSelected ? '2px' : '1px';
    const bgColor = isSelected ? '#FEF5EE' : 'white';
    const radioColor = isSelected ? '#F13E1A' : '#8A836C';
    const radioFill = isSelected ? `<div style="width: 8px; height: 8px; left: 7px; top: 7px; position: absolute; background: ${radioColor}; border-radius: 999px"></div>` : '';

    const benefitsHtml = this.config.benefits.map(benefit => `
      <div style="align-self: stretch; padding-left: 22px; position: relative; flex-direction: column; justify-content: flex-start; align-items: flex-start; display: flex;">
        <div style="width: 13.20px; height: 15px; left: 0px; top: 0px; position: absolute; color: #16A34B; font-size: 13px; font-family: Arial Black, sans-serif; font-weight: 900; line-height: 15.60px;">
          ${benefit.icon}
        </div>
        <div>
          <span style="color: #27272A; font-size: 13px; font-family: Poppins, sans-serif; font-weight: 500; line-height: 15.60px;">${benefit.text}</span>
          <span style="color: #4E4E56; font-size: 13px; font-family: Poppins, sans-serif; font-weight: 400; line-height: 15.60px;"> ${benefit.subtext}</span>
        </div>
      </div>
    `).join('');

    const periodSelectorHtml = this.renderPeriodSelector();
    const shipmentSimulatorHtml = isSelected ? this.renderShipmentSimulator() : '';

    return `
      <div class="bigens-option bigens-subscription ${isSelected ? 'selected' : ''}" 
           style="align-self: stretch; padding: 18px; background: ${bgColor}; border-radius: 14px; outline: ${borderWidth} ${borderColor} solid; outline-offset: -2px; flex-direction: column; gap: 18px; display: flex; cursor: pointer; transition: all 0.2s ease;"
           data-option-type="subscription">
        <div style="justify-content: flex-start; align-items: flex-start; gap: 18px; display: flex;">
          <div style="flex: 1; flex-direction: column; gap: 18px; display: flex;">
            <div style="justify-content: flex-start; align-items: center; gap: 18px; display: flex;">
              <div style="width: 22px; height: 24px; padding-top: 2px; flex-direction: column; justify-content: flex-start; align-items: flex-start; display: inline-flex;">
                <div style="width: 22px; height: 22px; position: relative; border-radius: 999px; outline: 2px ${radioColor} solid; outline-offset: -2px;">
                  ${radioFill}
                </div>
              </div>
              <div style="flex: 1; color: #27272A; font-size: 16px; font-family: Poppins, sans-serif; font-weight: 600; line-height: 15.40px;">
                Assine e economize 🧡
              </div>
            </div>
            <div style="flex-direction: column; gap: 4px; display: flex;">
              ${benefitsHtml}
            </div>
          </div>
          <div style="flex-direction: column; justify-content: center; align-items: flex-end; gap: 4px; display: flex;">
            <div style="padding: 4px 8px; background: #BBF7D1; border-radius: 24px;">
              <div style="color: #15803D; font-size: 14px; font-family: Poppins, sans-serif; font-weight: 600; line-height: 15.60px;">
                ${this.config.discountBadge}
              </div>
            </div>
          </div>
        </div>
        ${isSelected ? periodSelectorHtml + shipmentSimulatorHtml : ''}
      </div>
    `;
  }

  renderPeriodSelector() {
    if (this.state.variants.subscriptions.length === 0) return '';

    // Remove duplicatas - agrupa por período e pega apenas a primeira variante de cada
    const uniquePeriods = {};
    this.state.variants.subscriptions.forEach(variant => {
      if (!uniquePeriods[variant.period]) {
        uniquePeriods[variant.period] = variant;
      }
    });

    const periodsHtml = Object.values(uniquePeriods).map(variant => {
      const isSelected = this.state.selectedPeriod === variant.period;
      const bgColor = isSelected ? '#F13E1A' : 'white';
      const textColor = isSelected ? 'white' : '#27272A';
      const borderColor = isSelected ? '#F13E1A' : '#FACBAE';
      const fontWeight = isSelected ? '600' : '400';

      return `
        <div class="bigens-period-option ${isSelected ? 'selected' : ''}"
             style="flex: 1; min-width: 90px; height: 40px; padding: 11.20px 14px; background: ${bgColor}; border-radius: 999px; outline: 1px ${borderColor} solid; outline-offset: -1px; justify-content: center; align-items: center; display: flex; cursor: pointer; transition: all 0.2s ease;"
             data-period="${variant.period}"
             data-variant-id="${variant.id}">
          <div style="color: ${textColor}; font-size: 13.30px; font-family: Poppins, sans-serif; font-weight: ${fontWeight}; white-space: nowrap;">
            ${variant.period} dias
          </div>
        </div>
      `;
    }).join('');

    return `
      <div style="flex-direction: column; gap: 10px; display: flex; margin-top: 16px;">
        <div style="color: #27272A; font-size: 14px; font-family: Poppins, sans-serif; font-weight: 600; line-height: 21px;">
          Receber a cada:
        </div>
        <div class="bigens-period-selector" style="justify-content: flex-start; align-items: center; gap: 10px; display: flex; flex-wrap: nowrap; overflow-x: auto;">
          ${periodsHtml}
        </div>
      </div>
    `;
  }

  renderShipmentSimulator() {
    const today = new Date();
    const shipmentDates = this.calculateShipmentDates(today, this.state.selectedPeriod, 3);

    const shipmentsHtml = shipmentDates.map((date, index) => `
      <div style="flex: 1; flex-direction: column; gap: 8px; display: flex;">
        <div style="flex-direction: column; gap: 4px; display: flex;">
          <div style="color: #E7411B; font-size: 12px; font-family: Poppins, sans-serif; font-weight: 700; line-height: 12px;">
            ${index + 1}º ENVIO
          </div>
          <div style="color: #772117; font-size: 12px; font-family: Poppins, sans-serif; font-weight: 400; line-height: 12px;">
            ${this.formatDate(date)}
          </div>
        </div>
        <div style="justify-content: flex-start; align-items: center; display: flex;">
          <div style="width: 8px; height: 8px; background: #E7411B; border-radius: 9999px;"></div>
          <div style="flex: 1; height: 2px; background: white; border-radius: 16px;"></div>
        </div>
      </div>
    `).join('');

    return `
      <div style="padding: 16px; background: #FDE7D7; border-radius: 16px; flex-direction: column; gap: 10px; display: flex; margin-top: 16px;">
        <div style="color: #772117; font-size: 14px; font-family: Poppins, sans-serif; font-weight: 700; line-height: 21px;">
          Simulação de envios*
        </div>
        <div style="justify-content: flex-start; align-items: flex-start; display: flex; gap: 8px;">
          ${shipmentsHtml}
        </div>
        <div style="color: #772117; font-size: 12px; font-family: Poppins, sans-serif; font-weight: 400; line-height: 18px;">
          *As datas acima referem-se à renovação do pedido e não ao prazo de entrega.
        </div>
      </div>
    `;
  }

  render() {
    const container = document.querySelector(this.config.containerSelector);
    if (!container) {
      console.warn('Container do seletor de assinatura não encontrado:', this.config.containerSelector);
      return;
    }

    const html = `
      <div class="bigens-subscription-wrapper" style="width: 100%; flex-direction: column; gap: 8px; display: flex; font-family: Poppins, sans-serif;">
        ${this.renderOneTimeOption()}
        ${this.renderSubscriptionOption()}
      </div>
    `;

    container.innerHTML = html;
  }

  attachEventListeners() {
    const container = document.querySelector(this.config.containerSelector);
    if (!container) return;

    // Click em opção de compra única
    const oneTimeOption = container.querySelector('.bigens-one-time');
    if (oneTimeOption) {
      oneTimeOption.addEventListener('click', () => this.selectOneTime());
    }

    // Click em opção de assinatura
    const subscriptionOption = container.querySelector('.bigens-subscription');
    if (subscriptionOption) {
      subscriptionOption.addEventListener('click', (e) => {
        // Não ativar se clicar diretamente nos botões de período
        if (!e.target.closest('.bigens-period-option')) {
          this.selectSubscription();
        }
      });
    }

    // Click em opções de período
    const periodOptions = container.querySelectorAll('.bigens-period-option');
    periodOptions.forEach(option => {
      option.addEventListener('click', (e) => {
        e.stopPropagation();
        const period = parseInt(option.dataset.period);
        const variantId = option.dataset.variantId;
        this.selectPeriod(period, variantId);
      });
    });
  }

  selectOneTime() {
    this.state.selectedType = 'one-time';
    this.render();
    this.attachEventListeners();
    this.updateShopifyVariant(this.state.variants.oneTime?.id);
    this.updateAddToCartButton(); // v1.1
  }

  selectSubscription() {
    this.state.selectedType = 'subscription';
    // Seleciona o primeiro período se ainda não tiver um selecionado
    if (!this.state.selectedPeriod && this.state.variants.subscriptions.length > 0) {
      this.state.selectedPeriod = this.state.variants.subscriptions[0].period;
    }
    this.render();
    this.attachEventListeners();
    
    // Atualiza para a variante de assinatura selecionada
    const selectedVariant = this.state.variants.subscriptions.find(
      v => v.period === this.state.selectedPeriod
    );
    if (selectedVariant) {
      this.updateShopifyVariant(selectedVariant.id);
    }
    this.updateAddToCartButton(); // v1.1
  }

  selectPeriod(period, variantId) {
    this.state.selectedPeriod = period;
    this.render();
    this.attachEventListeners();
    this.updateShopifyVariant(variantId);
    this.updateAddToCartButton(); // v1.1
  }

  updateShopifyVariant(variantId) {
    if (!variantId) return;

    console.log('Atualizando variante para:', variantId);

    // Método 1: Atualiza via seletor de variantes do tema
    const variantPicker = document.querySelector(this.config.variantPickerSelector);
    if (variantPicker) {
      const radioInput = variantPicker.querySelector(`input[value="${variantId}"]`);
      if (radioInput) {
        radioInput.checked = true;
        radioInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // Método 2: Atualiza campo hidden do formulário
    const productForm = document.querySelector(this.config.productFormSelector);
    if (productForm) {
      const variantInput = productForm.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = variantId;
        // Dispara evento de mudança no input para notificar outros scripts
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // Método 3: Compatibilidade com Kaching Bundle
    // O Kaching Bundle escuta mudanças de variante através de vários métodos
    this.notifyKachingBundle(variantId);

    // Método 4: Dispara evento customizado para outros scripts
    document.dispatchEvent(new CustomEvent('bigens:variant-changed', {
      detail: { variantId: variantId }
    }));

    // Método 5: Evento padrão Shopify para mudança de variante
    document.dispatchEvent(new CustomEvent('variant:change', {
      detail: { variant: variantId }
    }));

    // Atualiza URL sem recarregar a página
    const url = new URL(window.location);
    url.searchParams.set('variant', variantId);
    window.history.replaceState({}, '', url);
  }

  notifyKachingBundle(variantId) {
    // Método 1: Atualiza o elemento kaching-app-bundle diretamente
    const kachingBundle = document.querySelector('kaching-app-bundle');
    if (kachingBundle) {
      // Força atualização do Kaching Bundle
      if (typeof kachingBundle.updateVariant === 'function') {
        kachingBundle.updateVariant(variantId);
      }
      
      // Dispara evento que o Kaching escuta
      kachingBundle.dispatchEvent(new CustomEvent('variant-change', {
        detail: { variantId: variantId },
        bubbles: true
      }));
    }

    // Método 2: Busca por selects ou radios do Kaching e atualiza
    const kachingSelects = document.querySelectorAll('[data-kaching-bundle-select]');
    kachingSelects.forEach(select => {
      const option = select.querySelector(`option[value="${variantId}"]`);
      if (option) {
        select.value = variantId;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    // Método 3: API global do Kaching (se disponível)
    if (window.KachingAppBundle) {
      try {
        // Alguns métodos possíveis da API do Kaching
        if (typeof window.KachingAppBundle.setVariant === 'function') {
          window.KachingAppBundle.setVariant(variantId);
        }
        if (typeof window.KachingAppBundle.updatePrice === 'function') {
          window.KachingAppBundle.updatePrice();
        }
        if (typeof window.KachingAppBundle.refresh === 'function') {
          window.KachingAppBundle.refresh();
        }
      } catch (e) {
        console.warn('Erro ao atualizar Kaching Bundle:', e);
      }
    }

    // Método 4: Dispara evento no document para o Kaching escutar
    document.dispatchEvent(new CustomEvent('kaching:variant-change', {
      detail: { variantId: variantId },
      bubbles: true
    }));

    // Método 5: Pequeno delay para garantir que o Kaching processe
    setTimeout(() => {
      // Tenta encontrar e clicar em elementos de atualização do Kaching
      const kachingUpdateBtn = document.querySelector('[data-kaching-update], .kaching-update-btn');
      if (kachingUpdateBtn && typeof kachingUpdateBtn.click === 'function') {
        kachingUpdateBtn.click();
      }
    }, 50);
  }

  observeVariantChanges() {
    // Observa mudanças no seletor de variantes do tema (caso o usuário use outro método)
    const productForm = document.querySelector(this.config.productFormSelector);
    if (!productForm) return;

    // Observer 1: Mudanças no input hidden do formulário
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
          const variantId = parseInt(mutation.target.value);
          this.syncStateWithVariant(variantId);
        }
      });
    });

    const variantInput = productForm.querySelector('input[name="id"]');
    if (variantInput) {
      observer.observe(variantInput, { attributes: true });
      
      // Também escuta eventos de change
      variantInput.addEventListener('change', (e) => {
        const variantId = parseInt(e.target.value);
        if (!isNaN(variantId)) {
          this.syncStateWithVariant(variantId);
        }
      });
    }

    // Observer 2: Escuta eventos do Kaching Bundle
    document.addEventListener('kaching:variant-change', (e) => {
      if (e.detail && e.detail.variantId) {
        console.log('Mudança detectada do Kaching Bundle:', e.detail.variantId);
        this.syncStateWithVariant(e.detail.variantId);
      }
    });

    // Observer 3: Escuta mudanças no elemento kaching-app-bundle
    const kachingBundle = document.querySelector('kaching-app-bundle');
    if (kachingBundle) {
      const kachingObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          // Tenta capturar mudanças de atributo de variante
          if (mutation.type === 'attributes' && 
              (mutation.attributeName === 'data-variant-id' || 
               mutation.attributeName === 'variant-id')) {
            const variantId = parseInt(
              kachingBundle.getAttribute('data-variant-id') || 
              kachingBundle.getAttribute('variant-id')
            );
            if (!isNaN(variantId)) {
              console.log('Variante atualizada pelo Kaching:', variantId);
              this.syncStateWithVariant(variantId);
            }
          }
        });
      });

      kachingObserver.observe(kachingBundle, { 
        attributes: true,
        attributeFilter: ['data-variant-id', 'variant-id']
      });

      // Também escuta eventos customizados no elemento
      kachingBundle.addEventListener('variant-change', (e) => {
        if (e.detail && e.detail.variantId) {
          console.log('Evento variant-change do Kaching:', e.detail.variantId);
          this.syncStateWithVariant(e.detail.variantId);
        }
      });
    }

    // Observer 4: Observa mudanças nos selects/radios de variante do tema
    const variantRadios = document.querySelectorAll('input[name="id"], input[type="radio"][name*="option"]');
    variantRadios.forEach(radio => {
      radio.addEventListener('change', () => {
        // Pequeno delay para o formulário processar
        setTimeout(() => {
          const formVariantInput = document.querySelector('input[name="id"]');
          if (formVariantInput) {
            const variantId = parseInt(formVariantInput.value);
            if (!isNaN(variantId)) {
              this.syncStateWithVariant(variantId);
            }
          }
        }, 50);
      });
    });
  }

  syncStateWithVariant(variantId) {
    // Verifica se a variante alterada é de assinatura ou compra única
    if (this.state.variants.oneTime && this.state.variants.oneTime.id === variantId) {
      if (this.state.selectedType !== 'one-time') {
        this.state.selectedType = 'one-time';
        this.render();
        this.attachEventListeners();
        this.updateAddToCartButton(); // v1.1
      }
    } else {
      const subVariant = this.state.variants.subscriptions.find(v => v.id === variantId);
      if (subVariant) {
        if (this.state.selectedType !== 'subscription' || this.state.selectedPeriod !== subVariant.period) {
          this.state.selectedType = 'subscription';
          this.state.selectedPeriod = subVariant.period;
          this.render();
          this.attachEventListeners();
          this.updateAddToCartButton(); // v1.1
        }
      }
    }
  }

  // Método público para reinicializar
  refresh() {
    this.loadProductData();
    this.identifyVariants();
    this.render();
    this.attachEventListeners();
  }
// ─── NOVOS MÉTODOS v1.1: Label do botão + Modal de termos ────────────────

  _getAddToCartTextSpan() {
    // Estrutura do Horizon: button[name="add"] > .add-to-cart-text > .add-to-cart-text__content > span > span
    const horizonSpan = document.querySelector(
      'button[name="add"] .add-to-cart-text__content span span,' +
      'button[name="add"] .add-to-cart-text__content span,' +
      'button[name="add"] .add-to-cart-text__content'
    );
    if (horizonSpan) return horizonSpan;
    return document.querySelector(
      '.product-form__submit .btn-text,' +
      'button[name="add"] .btn__text,' +
      'button[name="add"]'
    );
  }

  updateAddToCartButton() {
    const isSubscription = this.state.selectedType === 'subscription';
    const textEl = this._getAddToCartTextSpan();
    if (!textEl) return;
    if (!textEl.dataset.bigensOriginalLabel) {
      textEl.dataset.bigensOriginalLabel = textEl.textContent.trim();
    }
    textEl.textContent = isSubscription
      ? 'Assinar agora'
      : textEl.dataset.bigensOriginalLabel;
  }

  interceptAddToCart() {
    document.addEventListener('click', (e) => {
      if (this.state.selectedType !== 'subscription') return;
      const btn = e.target.closest('button[name="add"]');
      if (!btn) return;
      const isAddToCart =
        btn.closest('product-form-component') ||
        btn.closest('form[action="/cart/add"]') ||
        btn.closest('.shopify-product-form') ||
        btn.dataset.testid === 'standalone-add-to-cart';
      if (!isAddToCart) return;
      if (btn.dataset.bigensConfirmed === 'true') {
        delete btn.dataset.bigensConfirmed;
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      this.state.pendingBtn = btn;
      this.openTermsModal();
    }, true);
  }

  openTermsModal() {
    // Cria o modal se ainda não existir
    if (!document.getElementById('bigens-terms-modal')) {
      const overlay = document.createElement('div');
      overlay.id = 'bigens-terms-modal';
      overlay.innerHTML = this._termsModalHTML();
      document.body.appendChild(overlay);
      // Aplica estilos inline para não depender de CSS externo
      this._applyModalStyles();
    }
    const overlay = document.getElementById('bigens-terms-modal');
    overlay.style.display = 'flex';
    document.body.style.overflow = 'hidden';
    setTimeout(() => overlay.classList.add('bigens-modal--open'), 10);

    // Event listeners do modal
    overlay.querySelector('[data-bigens-modal-confirm]')
      .addEventListener('click', () => this._confirmTerms());
    overlay.querySelector('[data-bigens-modal-cancel]')
      .addEventListener('click', () => this._closeTermsModal());
    overlay.querySelector('[data-bigens-modal-close]')
      .addEventListener('click', () => this._closeTermsModal());
    overlay.addEventListener('click', (e) => {
      if (e.target === overlay) this._closeTermsModal();
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this._closeTermsModal();
    }, { once: true });
  }

  _closeTermsModal() {
    const overlay = document.getElementById('bigens-terms-modal');
    if (!overlay) return;
    overlay.classList.remove('bigens-modal--open');
    setTimeout(() => { overlay.style.display = 'none'; }, 280);
    document.body.style.overflow = '';
    this.state.pendingBtn = null;
  }

  _confirmTerms() {
    const pendingBtn = this.state.pendingBtn;
    this._closeTermsModal();
    const btn = pendingBtn || document.querySelector(
      'button[name="add"][data-testid="standalone-add-to-cart"],' +
      'button[name="add"]'
    );
    if (btn) {
      btn.dataset.bigensConfirmed = 'true';
      btn.click();
    }
  }

  _termsModalHTML() {
    const periodLabel = { 30: 'mensal (a cada 30 dias)', 60: 'bimestral (a cada 60 dias)', 90: 'trimestral (a cada 90 dias)' }[this.state.selectedPeriod] || `a cada ${this.state.selectedPeriod} dias`;
    const badge = this.config.discountBadge || '10% OFF';
    return `
      <div class="bigens-modal" role="dialog" aria-modal="true" aria-labelledby="bigens-modal-title">
        <div class="bigens-modal__header">
          <h2 id="bigens-modal-title" class="bigens-modal__title">Termos da Assinatura</h2>
          <button class="bigens-modal__close" data-bigens-modal-close aria-label="Fechar" type="button">✕</button>
        </div>
        <div class="bigens-modal__body">
          <div class="bigens-modal__terms">
            <p>Ao confirmar sua assinatura, você está de acordo com o seguinte:</p>
            <ul>
              <li><strong>Desconto garantido:</strong> O desconto da assinatura vale em todas as renovações enquanto sua assinatura estiver ativa.</li>
              <li><strong>Cancelamento sem burocracia:</strong> Você pode cancelar ou alterar sua assinatura a qualquer momento, sem multa. É só acessar o portal do cliente ou falar com nosso suporte.</li>
              <li><strong>Entrega recorrente:</strong> Seus pedidos são processados e enviados conforme a frequência que você escolheu. O prazo de entrega começa a contar após o processamento.</li>
              <li><strong>Quer mudar a frequência?</strong> É só solicitar antes da próxima renovação.</li>
              <li><strong>Pagamento fácil e automático:</strong> A cobrança é feita direto no cartão ou método de pagamento cadastrado na frequência selecionada. Se houver algum problema, a gente te avisa para atualizar os dados.</li>
            </ul>
            <p style="font-size:12px;color:#8A836C;margin:0;">Ficou com alguma dúvida? <a href="https://api.whatsapp.com/send?phone=5511939422905" target="_blank" style="color:#E7411B;">Fale com a gente pelo WhatsApp.</a></p>
          </div>
        </div>
        <div class="bigens-modal__footer">
          <button class="bigens-modal__btn bigens-modal__btn--cancel" data-bigens-modal-cancel type="button">Cancelar</button>
          <button class="bigens-modal__btn bigens-modal__btn--confirm" data-bigens-modal-confirm type="button">Aceitar e assinar</button>
        </div>
      </div>
    `;
  }

  _applyModalStyles() {
    if (document.getElementById('bigens-modal-styles')) return;
    const style = document.createElement('style');
    style.id = 'bigens-modal-styles';
    style.textContent = `
      #bigens-terms-modal {
        display: none;
        position: fixed;
        inset: 0;
        z-index: 99999;
        background: rgba(0,0,0,0.5);
        align-items: flex-end;
        justify-content: center;
        opacity: 0;
        transition: opacity 0.25s ease;
        backdrop-filter: blur(2px);
        -webkit-backdrop-filter: blur(2px);
        font-family: 'Poppins', sans-serif;
      }
      #bigens-terms-modal.bigens-modal--open { opacity: 1; }
      .bigens-modal {
        width: 100%;
        max-width: 540px;
        max-height: 85vh;
        background: #fff;
        border-radius: 20px 20px 0 0;
        display: flex;
        flex-direction: column;
        transform: translateY(30px);
        transition: transform 0.3s cubic-bezier(0.4,0,0.2,1);
        overflow: hidden;
      }
      #bigens-terms-modal.bigens-modal--open .bigens-modal { transform: translateY(0); }
      @media (min-width: 600px) {
        #bigens-terms-modal { align-items: center; }
        .bigens-modal { border-radius: 20px; max-height: 80vh; margin: 16px; }
      }
      .bigens-modal__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 20px 20px 16px;
        border-bottom: 1px solid #DADADD;
        flex-shrink: 0;
      }
      .bigens-modal__title {
        font-size: 17px;
        font-weight: 700;
        color: #27272A;
        margin: 0;
        font-family: 'Poppins', sans-serif;
      }
      .bigens-modal__close {
        width: 32px; height: 32px;
        border-radius: 50%; border: none;
        background: #F7F7F8; color: #606169;
        font-size: 14px; cursor: pointer;
        display: flex; align-items: center; justify-content: center;
        transition: background 0.15s ease;
      }
      .bigens-modal__close:hover { background: #DADADD; }
      .bigens-modal__body {
        overflow-y: auto; padding: 20px; flex: 1;
        -webkit-overflow-scrolling: touch;
      }
      .bigens-modal__terms { font-size: 14px; line-height: 1.6; color: #4E4E56; }
      .bigens-modal__terms p { margin: 0 0 12px; color: #27272A; }
      .bigens-modal__terms ul { margin: 0 0 16px; padding-left: 20px; display: flex; flex-direction: column; gap: 10px; }
      .bigens-modal__terms li { color: #4E4E56; }
      .bigens-modal__terms li strong { color: #27272A; }
      .bigens-modal__footer {
        display: flex; gap: 10px;
        padding: 16px 20px;
        padding-bottom: max(16px, env(safe-area-inset-bottom));
        border-top: 1px solid #DADADD;
        flex-shrink: 0;
      }
      .bigens-modal__btn {
        flex: 1; height: 48px; border-radius: 10px; border: none;
        font-family: 'Poppins', sans-serif; font-size: 14px; font-weight: 600;
        cursor: pointer; transition: all 0.2s ease;
      }
      .bigens-modal__btn--cancel {
        background: #F7F7F8; color: #4E4E56; border: 1.5px solid #DADADD;
      }
      .bigens-modal__btn--cancel:hover { background: #DADADD; }
      .bigens-modal__btn--confirm { background: #E7411B; color: #fff; }
      .bigens-modal__btn--confirm:hover {
        background: #c93618;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(231,65,27,0.3);
      }
    `;
    document.head.appendChild(style);
  }

}

// Exporta para uso global
window.BigensSubscriptionSelector = BigensSubscriptionSelector;

// Auto-inicialização quando o DOM estiver pronto
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    if (document.querySelector('#bigens-subscription-selector')) {
      window.bigensSubscriptionSelector = new BigensSubscriptionSelector();
    }
  });
} else {
  if (document.querySelector('#bigens-subscription-selector')) {
    window.bigensSubscriptionSelector = new BigensSubscriptionSelector();
  }
}
